import { getChatApiRoute } from '../api/v1Client'
import { useAuth } from '../providers'
import { ChatError } from '../types'
import { getRayId } from '../utils'

interface UploadFileOptions {
  /**
   * The name of the file to upload. Will be used for the LLM to reference the file.
   */
  name?: string
  mimeType: string
  data: Uint8Array
}

export const useUploadFile = () => {
  const { getAuthToken } = useAuth()

  /**
   * Uploads a file to the server and returns the file id.
   */
  return async ({ name, mimeType, data }: UploadFileOptions): Promise<string> => {
    // If file name is too long, we won't send it to the server
    let fileName = (name?.length ?? 0) >= 50 ? undefined : name
    if (fileName === '') fileName = undefined

    try {
      const prepareResponse = await fetch(getChatApiRoute('api/v1/files/prepare'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: fileName,
          type: mimeType,
          size: data.byteLength,
        }),
      })

      if (!prepareResponse.ok) {
        let backendError = 'Unknown error'
        try {
          const errJson = await prepareResponse.json()
          backendError = errJson?.error ?? backendError
        } catch (_) {}

        console.error('Failed to prepare file for upload:', backendError)

        throw new ChatError(`Failed to prepare file for upload: ${backendError}`, {
          chatId: 'upload-file',
          userMessageId: null,
          assistantMessageId: null,
          errorCode: 'file-prepare-error',
          rayId: getRayId(prepareResponse.headers),
        })
      }

      const prepareResult = (await prepareResponse.json()).data as {
        id: string
        uploadUrl: string
      }

      const fileId = prepareResult.id
      const uploadUrl = prepareResult.uploadUrl

      const arrayBuffer: ArrayBuffer =
        data.byteOffset === 0 && data.buffer instanceof ArrayBuffer ? data.buffer : data.slice().buffer // copies just the viewed bytes; .buffer is ArrayBuffer

      const response = await fetch(uploadUrl, {
        method: 'PUT',
        body: arrayBuffer,
        headers: {
          Authorization: `Bearer ${await getAuthToken()}`,
        },
      })

      if (!response.ok) {
        let errorMessage = `Upload failed with status ${response.status}`
        try {
          const errorData = await response.json()
          errorMessage = `Failed to upload file: ${errorData.error || response.statusText || errorMessage}`
        } catch (e) {
          // If we can't parse the JSON, just use the status text
          errorMessage = `Failed to upload file: ${response.statusText || errorMessage}`
        }
        console.error(errorMessage)

        throw new ChatError(errorMessage, {
          chatId: 'upload-file',
          userMessageId: null,
          assistantMessageId: null,
          errorCode: 'file-upload-error',
          rayId: getRayId(response.headers),
        })
      }

      return fileId
    } catch (e) {
      // The prepare/upload branches above already threw a ChatError carrying
      // the backend's reason; wrapping it again would bury that reason.
      if (e instanceof ChatError) {
        throw e
      }
      throw new ChatError(`File upload failed: ${String(e)}`, {
        chatId: 'upload-file',
        userMessageId: null,
        assistantMessageId: null,
        errorCode: 'file-upload-error',
        rayId: 'Not available',
      })
    }
  }
}
